import { Box, Grid, LinearProgress as MuiLinearProgress, styled } from "@mui/material";
import { StriimTypography } from "@striim/striim-ui";
import React, { useCallback, useEffect, useState, useRef } from "react";
import WithTheme from "src/generic/theme/theme";
import LoadingIndicator from "../../../../../../../../../src/generic/loading-indicator";
import App from "app";
import ErrorIcon from "../../../../../../../../../src/generic/icon/wizard-icon/errorFilled.svg";
import { styles } from "./schema-validation-progress-bar.styles";
import { executeValidationMethods } from "./schema-validation-progress-bar.utils";
import { SchemaModel } from "../types";

export interface ValidationStepProps {
    id: number;
    title: string;
    method: (schema: SchemaModel) => Promise<void>;
    statusKey: string;
    errors: boolean;
}

interface SchemaValidationProgressBarReactViewProps {
    validationSteps: ValidationStepProps[];
    selectedSchemas: SchemaModel[];
    currentStep: string;
}

const SchemaValidationProgressBarReactView: React.FC<SchemaValidationProgressBarReactViewProps> = ({
    validationSteps,
    selectedSchemas,
    currentStep
}) => {
    const [percentage, setPercentage] = useState(0);
    const [validationStep, setValidationStep] = useState(validationSteps[0]?.title || "");
    const [validationErrs, setValidationErrs] = useState([]);
    const [validationIsExecuting, setValidationIsExecuting] = useState(false);
    const abortControllerRef = useRef(null);
    const [completedCount, setCompletedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        abortControllerRef.current = new AbortController();
        executeValidations();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        const handleRetry = () => {
            App.vent.trigger("appwizard:retry:hide");
            App.vent.trigger("appwizard:next:disable");
            executeValidations(true);
        };
        $("#footer-retry-button").on("click", handleRetry);
        return () => $("#footer-retry-button").off("click", handleRetry);
    }, []);

    useEffect(() => {
        if (validationIsExecuting || percentage !== 100) return;
        if (validationErrs.length > 0) {
            App.vent.trigger("appwizard:retry:show");
        } else {
            App.vent.trigger("appwizard:goForward", currentStep);
        }
    }, [percentage, validationErrs, currentStep, validationIsExecuting]);

    const executeValidations = async (isRetry = false) => {
        const signal = abortControllerRef.current.signal;
        setValidationErrs([]);
        setValidationIsExecuting(true);
        for (const step of validationSteps) {
            if (signal.aborted) return;
            const schemasToValidate = isRetry
                ? selectedSchemas.filter(schema => schema.get(step.statusKey) === false)
                : selectedSchemas;
            if (schemasToValidate.length === 0) {
                continue;
            }
            setPercentage(0);
            setCompletedCount(0);
            setTotalCount(schemasToValidate.length);
            setValidationStep(step.title);
            await executeValidationMethods(
                step,
                schemasToValidate,
                setPercentage,
                setValidationErrs,
                signal,
                setCompletedCount
            );
            await new Promise(requestAnimationFrame);
        }
        setValidationIsExecuting(false);
    };

    const ValidationProgressBar = useCallback(
        () => (
            <Grid item container flexDirection="column" gap={1}>
                <Grid item display={"flex"} justifyContent={"space-between"} alignItems={"center"} gap={1}>
                    <StriimTypography variant="h4" color="primary.700">
                        {validationStep} - {percentage}%
                    </StriimTypography>
                    {totalCount > 0 && (
                        <StriimTypography variant="caption2" color="primary.700">
                            {completedCount} / {totalCount}
                        </StriimTypography>
                    )}
                </Grid>
                <LoadingIndicator
                    isGlobal={false}
                    variant="determinate"
                    value={percentage}
                    sx={{
                        height: 8,
                        borderRadius: 8,
                        backgroundColor: "greyscale.100",
                        "& .MuiLinearProgress-bar": {
                            ...(validationErrs.length > 0 && { backgroundColor: "critical.500" })
                        }
                    }}
                />
            </Grid>
        ),
        [percentage, validationStep, validationErrs, completedCount, totalCount]
    );

    const ValidationErrsList = useCallback(() => {
        if (!validationErrs.length) return null;

        const renderErrors = (error, index) => {
            return (
                <Grid container sx={styles.gridContainerBase} key={index}>
                    <Grid item sx={styles.gridstatus}>
                        <ErrorIcon style={styles.svgIcon} />
                    </Grid>
                    <Grid item sx={styles.gridItemBase}>
                        <StriimTypography variant="body4" sx={styles.typography}>
                            {error.message}
                        </StriimTypography>
                        <StriimTypography variant="body4" sx={styles.typography}>
                            {error.remedy}{" "}
                        </StriimTypography>
                    </Grid>
                </Grid>
            );
        };
        return (
            <Grid item container flexDirection="row" sx={{ maxHeight: "450px", overflowY: "auto" }}>
                {validationErrs.map((err, idx) => renderErrors(err, idx))}{" "}
            </Grid>
        );
    }, [validationErrs]);

    return (
        <Grid container flexDirection="column" gap={3} sx={{ height: "100%" }}>
            <ValidationProgressBar />
            <ValidationErrsList />
        </Grid>
    );
};

export default WithTheme(SchemaValidationProgressBarReactView);
